<script setup lang="ts">
import { message, TimePicker } from 'ant-design-vue'
import { computed, ref, watch } from 'vue'
import type { Booking, Property } from '@/models'
import { searchGuests } from '@/services/guests'

const props = defineProps<{
  booking: Booking | null
  properties: Property[]
  saving?: boolean
}>()

const emit = defineEmits<{
  save: [payload: Partial<Booking>]
}>()

const visible = defineModel<boolean>()

const formRef = ref()
const form = ref<Partial<Booking> & { check_in_time?: string; check_out_time?: string }>({
  property_id: '',
  guest_name: '',
  guest_email: '',
  guest_phone: '',
  guests_count: 1,
  check_in: '',
  check_out: '',
  check_in_time: undefined,
  check_out_time: undefined,
  status: 'confirmed',
  notes: '',
  guest_id: undefined,
  rental_type: 'nightly',
  monthly_rate: undefined,
  rent_due_day: 1,
  security_deposit: undefined,
  contract_ref: undefined,
  contract_end_date: undefined,
})

const guestOptions = ref<Array<{ value: string; label: string }>>([])
const guestSearching = ref(false)

const statusOptions = [
  { value: 'confirmed', label: 'Confirmed' },
  { value: 'pending', label: 'Pending' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'completed', label: 'Completed' },
  { value: 'no-show', label: 'No Show' },
]

const propertyOptions = computed(() => {
  return (props.properties || []).map((p) => ({
    value: p.id,
    label: `${p.title} — ${p.location}`,
  }))
})

const selectedProperty = computed(() =>
  props.properties.find((p) => p.id === form.value.property_id),
)

// When property changes, auto-fill times from property defaults if booking doesn't have override
watch(
  () => form.value.property_id,
  (newId) => {
    if (!newId) return
    // Only auto-fill if times are not already set (for new bookings)
    if (form.value.check_in_time === undefined && form.value.check_out_time === undefined) {
      const prop = props.properties.find((p) => p.id === newId) as any
      if (prop) {
        form.value.check_in_time = prop.check_in_time || '15:00'
        form.value.check_out_time = prop.check_out_time || '11:00'
      }
    }
  },
)

const onGuestSearch = async (query: string) => {
  if (!query || query.length < 2) {
    guestOptions.value = []
    return
  }
  guestSearching.value = true
  try {
    guestOptions.value = await searchGuests(query)
  } catch {
    guestOptions.value = []
  } finally {
    guestSearching.value = false
  }
}

const onGuestSelect = (_value: string | number | any, option: any) => {
  const label = option?.label || ''
  const match = label.match(/^(.+?)\s*\(/)
  form.value.guest_name = match ? match[1].trim() : label.trim()
}

watch(visible, (newVal) => {
  if (newVal) {
    const val = props.booking
    if (val) {
      form.value = {
        property_id: val.property_id || '',
        guest_name: val.guest_name || '',
        guest_email: val.guest_email || '',
        guest_phone: val.guest_phone || '',
        guests_count: val.guests_count || 1,
        check_in: val.check_in || '',
        check_out: val.check_out || '',
        check_in_time: (val as any).check_in_time || undefined,
        check_out_time: (val as any).check_out_time || undefined,
        status: val.status || 'confirmed',
        notes: val.notes || '',
        guest_id: val.guest_id || undefined,
        rental_type: val.rental_type || 'nightly',
        monthly_rate: val.monthly_rate ?? undefined,
        rent_due_day: val.rent_due_day || 1,
        security_deposit: val.security_deposit ?? undefined,
        contract_ref: val.contract_ref ?? undefined,
        contract_end_date: val.contract_end_date ?? undefined,
      }
      // If booking has no override, show property default as placeholder but keep undefined so it falls back
      if (!form.value.check_in_time || !form.value.check_out_time) {
        const prop = props.properties.find((p) => p.id === val.property_id) as any
        if (prop) {
          if (!form.value.check_in_time) form.value.check_in_time = prop.check_in_time || undefined
          if (!form.value.check_out_time)
            form.value.check_out_time = prop.check_out_time || undefined
        }
      }
    } else {
      form.value = {
        property_id: '',
        guest_name: '',
        guest_email: '',
        guest_phone: '',
        guests_count: 1,
        check_in: '',
        check_out: '',
        check_in_time: undefined,
        check_out_time: undefined,
        status: 'confirmed',
        notes: '',
        guest_id: undefined,
        rental_type: 'nightly',
        monthly_rate: undefined,
        rent_due_day: 1,
        security_deposit: undefined,
        contract_ref: undefined,
        contract_end_date: undefined,
      }
    }
    formRef.value?.clearValidate?.()
  }
})

const rules = {
  property_id: [{ required: true, message: 'Please select a property' }],
  guest_name: [{ required: true, message: 'Please enter guest name' }],
  guest_email: [{ type: 'email' as const, message: 'Please enter a valid email' }],
  check_in: [{ required: true, message: 'Please select check-in date' }],
  check_out: [
    { required: true, message: 'Please select check-out date' },
    {
      validator: (_rule: any, value: string) => {
        if (!value || !form.value.check_in) return Promise.resolve()
        if (value <= form.value.check_in) {
          return Promise.reject(new Error('Check-out must be after check-in'))
        }
        return Promise.resolve()
      },
    },
  ],
}

const handleSubmit = async () => {
  await formRef.value?.validate()
  const payload: any = { ...form.value }
  if (!payload.guest_id) {
    delete payload.guest_id
  }
  // Allow clearing override: empty -> null (falls back to property default)
  if (!payload.check_in_time) {
    payload.check_in_time = null
  }
  if (!payload.check_out_time) {
    payload.check_out_time = null
  }
  emit('save', payload)
}

const handleClose = () => {
  visible.value = false
  formRef.value?.resetFields()
}
</script>

<template>
  <a-modal
    :open="visible"
    :title="booking ? 'Edit Booking' : 'New Booking'"
    @ok="handleSubmit"
    @cancel="handleClose"
    :confirm-loading="saving"
    ok-text="Save"
    :width="560"
  >
    <a-form ref="formRef" :model="form" :rules="rules" layout="vertical">
      <a-form-item label="Property" name="property_id">
        <a-select
          v-model:value="form.property_id"
          :options="propertyOptions"
          show-search
          :filter-option="(input: string, option: any) => option.label.toLowerCase().includes(input.toLowerCase())"
          placeholder="Select a property"
        />
      </a-form-item>

      <a-form-item label="Link to Guest (optional)">
        <a-select
          v-model:value="form.guest_id"
          :options="guestOptions"
          show-search
          :filter-option="false"
          :loading="guestSearching"
          @search="onGuestSearch"
          @select="onGuestSelect"
          @clear="guestOptions = []"
          allow-clear
          placeholder="Search and select a guest..."
          not-found-content="Type to search guests"
        />
      </a-form-item>

      <a-row :gutter="12">
        <a-col :span="12">
          <a-form-item label="Guest Name" name="guest_name">
            <a-input v-model:value="form.guest_name" placeholder="Guest name" />
          </a-form-item>
        </a-col>
        <a-col :span="12">
          <a-form-item label="Guests Count" name="guests_count">
            <a-input-number
              v-model:value="form.guests_count"
              :min="1"
              :max="100"
              style="width: 100%"
            />
          </a-form-item>
        </a-col>
      </a-row>

      <a-row :gutter="12">
        <a-col :span="12">
          <a-form-item label="Email" name="guest_email">
            <a-input v-model:value="form.guest_email" placeholder="guest@email.com" />
          </a-form-item>
        </a-col>
        <a-col :span="12">
          <a-form-item label="Phone" name="guest_phone">
            <a-input v-model:value="form.guest_phone" placeholder="Phone number" />
          </a-form-item>
        </a-col>
      </a-row>

      <a-row :gutter="12">
        <a-col :span="12">
          <a-form-item label="Check In" name="check_in">
            <a-date-picker
              v-model:value="form.check_in"
              style="width: 100%"
              value-format="YYYY-MM-DD"
            />
          </a-form-item>
        </a-col>
        <a-col :span="12">
          <a-form-item label="Check Out" name="check_out">
            <a-date-picker
              v-model:value="form.check_out"
              style="width: 100%"
              value-format="YYYY-MM-DD"
            />
          </a-form-item>
        </a-col>
      </a-row>

      <a-row :gutter="12">
        <a-col :span="12">
          <a-form-item label="Check-in Time" extra="Override property default (leave empty to use property setting)">
            <a-time-picker
              v-model:value="form.check_in_time"
              format="HH:mm"
              valueFormat="HH:mm"
              style="width: 100%"
              :placeholder="selectedProperty?.check_in_time || '15:00'"
              allow-clear
            />
          </a-form-item>
        </a-col>
        <a-col :span="12">
          <a-form-item label="Check-out Time" extra="Override property default">
            <a-time-picker
              v-model:value="form.check_out_time"
              format="HH:mm"
              valueFormat="HH:mm"
              style="width: 100%"
              :placeholder="selectedProperty?.check_out_time || '11:00'"
              allow-clear
            />
          </a-form-item>
        </a-col>
      </a-row>

      <a-form-item label="Rental Type" name="rental_type">
        <a-select
          v-model:value="form.rental_type"
          :options="[
            { value: 'nightly', label: 'Nightly' },
            { value: 'monthly', label: 'Monthly' },
            { value: 'lease', label: 'Lease' },
          ]"
          style="width: 200px"
        />
      </a-form-item>

      <template v-if="form.rental_type === 'monthly'">
        <a-row :gutter="12">
          <a-col :span="12">
            <a-form-item label="Monthly Rate (₱)" name="monthly_rate">
              <a-input-number v-model:value="form.monthly_rate" :min="0" style="width: 100%" />
            </a-form-item>
          </a-col>
          <a-col :span="12">
            <a-form-item label="Rent Due Day" name="rent_due_day">
              <a-input-number v-model:value="form.rent_due_day" :min="1" :max="31" style="width: 100%" />
            </a-form-item>
          </a-col>
        </a-row>
      </template>

      <template v-if="form.rental_type === 'lease'">
        <a-row :gutter="12">
          <a-col :span="12">
            <a-form-item label="Monthly Rate (₱)" name="monthly_rate">
              <a-input-number v-model:value="form.monthly_rate" :min="0" style="width: 100%" />
            </a-form-item>
          </a-col>
          <a-col :span="12">
            <a-form-item label="Contract End" name="contract_end_date">
              <a-date-picker v-model:value="form.contract_end_date" style="width: 100%" value-format="YYYY-MM-DD" />
            </a-form-item>
          </a-col>
        </a-row>
        <a-row :gutter="12">
          <a-col :span="12">
            <a-form-item label="Security Deposit (₱)" name="security_deposit">
              <a-input-number v-model:value="form.security_deposit" :min="0" style="width: 100%" />
            </a-form-item>
          </a-col>
          <a-col :span="12">
            <a-form-item label="Contract Ref" name="contract_ref">
              <a-input v-model:value="form.contract_ref" placeholder="e.g. LEASE-2026-001" />
            </a-form-item>
          </a-col>
        </a-row>
      </template>

      <a-form-item label="Status" name="status">
        <a-select v-model:value="form.status" :options="statusOptions" style="width: 200px" />
      </a-form-item>

      <a-form-item label="Notes" name="notes">
        <a-textarea v-model:value="form.notes" :rows="3" placeholder="Optional notes" />
      </a-form-item>
    </a-form>
  </a-modal>
</template>
